import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { ShieldCheck, Search as SearchIcon, Filter, X } from "lucide-react";

const CATEGORIES = ["Female Model", "Male Model", "Actor", "Actress", "Dancer", "Creator", "Influencer", "Commercial Talent", "Fashion Model", "Fitness Model"];
const CITIES = ["Chandigarh", "Mohali", "Panchkula", "Delhi", "Mumbai", "Punjab", "Haryana", "Other"];
const SKILLS = ["Acting", "Dance", "Fashion", "Commercial", "Punjabi", "Hindi", "English", "Music Video", "Runway", "Print", "Digital", "Fitness"];

const PAGE_SIZE = 24;

export default function TalentDirectory() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    q: "", city: "", category: params.get("category") || "", skill: "", sort: "featured",
  });

  const buildQS = (extra = {}) => {
    const p = new URLSearchParams();
    Object.entries({ ...filters, ...extra }).forEach(([k, v]) => { if (v) p.append(k, v); });
    p.append("limit", PAGE_SIZE);
    return p.toString();
  };

  const load = useCallback(async (nextSkip = 0, append = false) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/talents?${buildQS({ skip: nextSkip })}`);
      const arr = data?.items || [];
      setItems(append ? [...items, ...arr] : arr);
      setTotal(data?.total || 0);
      setSkip(nextSkip);
    } finally { setLoading(false); }
  // eslint-disable-next-line
  }, [filters, items]);

  useEffect(() => { load(0, false); /* on mount + filter change */ // eslint-disable-next-line
  }, [filters]);

  const canLoadMore = skip + items.length < total;

  return (
    <div className="min-h-screen">
      <Header />
      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pt-20 pb-6">
        <div className="sv-overline mb-4">Our Talent</div>
        <h1 className="font-serif-hero text-4xl lg:text-6xl text-[#F4EFE4]">The SV directory.</h1>
        <p className="mt-4 text-[#CDC7BA] max-w-2xl">Explore models, actors, dancers, creators and performers represented by SV Talent Agency.</p>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pb-4">
        <div className="sv-card p-4 sm:p-5">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <SearchIcon size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8478]" />
              <input data-testid="talent-search" placeholder="Search by name, city or skill…" value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                className="sv-input pl-10" />
            </div>
            <select data-testid="filter-category" className="sv-select" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
              <option value="">All Categories</option>
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="filter-city" className="sv-select" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}>
              <option value="">All Locations</option>
              {CITIES.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="filter-skill" className="sv-select" value={filters.skill} onChange={(e) => setFilters({ ...filters, skill: e.target.value })}>
              <option value="">All Skills</option>
              {SKILLS.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select data-testid="filter-sort" className="sv-select" value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })}>
              <option value="featured">Featured</option>
              <option value="recent">Recently Added</option>
              <option value="az">A–Z</option>
            </select>
          </div>
        </div>
      </section>

      <section className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 pt-4 pb-16">
        <div className="text-sm text-[#8A8478] mb-4">{total.toLocaleString()} verified talent</div>
        {items.length === 0 && !loading ? (
          <div className="sv-card p-12 text-center">
            <div className="sv-overline mb-3">No profiles yet</div>
            <h3 className="font-serif-editorial text-2xl text-[#F4EFE4]">The directory is being curated.</h3>
            <p className="mt-3 text-sm text-[#CDC7BA] max-w-md mx-auto">Approved profiles appear here. Adjust filters, or apply to be featured.</p>
            <Link to="/join" className="sv-btn-primary inline-flex mt-6">Apply as Talent</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
            {items.map((t) => (
              <Link key={t.user_id} to={`/talent/${t.user_id}`} data-testid={`talent-card-${t.user_id}`} className="talent-card sv-card overflow-hidden">
                <div className="aspect-[3/4] overflow-hidden bg-[#131316]">
                  {t.data?.profile_photo ? <img src={t.data.profile_photo} className="w-full h-full object-cover" alt={t.full_name} loading="lazy" /> : <div className="w-full h-full grid place-items-center text-[#4A4842] font-mono-label text-[10px]">No Image</div>}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-serif-editorial text-base text-[#F4EFE4] truncate">{t.full_name}</div>
                    {t.featured && <ShieldCheck size={13} className="text-[#C5A059] flex-shrink-0" />}
                  </div>
                  <div className="text-[11px] text-[#8A8478] mt-1">{t.data?.categories?.[0]} · {t.data?.city}</div>
                  {t.data?.skills?.length > 0 && <div className="text-[10px] text-[#8A8478] mt-1 truncate">{t.data.skills.slice(0, 3).join(" · ")}</div>}
                </div>
              </Link>
            ))}
          </div>
        )}
        {canLoadMore && (
          <div className="text-center mt-10">
            <button onClick={() => load(skip + PAGE_SIZE, true)} disabled={loading} data-testid="load-more" className="sv-btn-outline">
              {loading ? "Loading…" : "Load More"}
            </button>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
